import { useState } from 'react';
import { useAuth } from '@clerk/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Group,
  Loader,
  Pagination,
  Paper,
  Rating,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconStar, IconUser } from '@tabler/icons-react';
import { useApi, useApiClient } from '../../api';
import RatingStars from './RatingStars';

const PAGE_SIZE = 10;

const formatDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Reviews section for the public seller detail page (contract §4.10).
//
// - Public paginated list via GET /api/v1/sellers/{id}/reviews (newest first).
// - `ratingAvg` / `ratingCount` come from the seller payload's aggregate fields.
// - Signed-in visitors get a "Write / update review" inline form. The list
//   carries no reviewer ids, so we cannot pre-fill "your" review — the form is
//   always blank and a successful PUT simply overwrites (one review per
//   reviewer+seller, server-side).
// - 403 (code "forbidden") means no prior interaction with the seller — we
//   explain that instead of showing a raw error.
// - "Delete my review" calls the idempotent DELETE (204 even when none existed).
const SellerReviews = ({ sellerId, ratingAvg = null, ratingCount = 0 }) => {
  const { isSignedIn } = useAuth();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const { data, loading, error, refetch } = useApi(
    `/api/v1/sellers/${sellerId}/reviews`,
    { page, limit: PAGE_SIZE },
  );

  const [formOpen, setFormOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refreshAfterMutation = () => {
    refetch();
    // The seller payload carries rating_avg/rating_count — refresh those too.
    queryClient.invalidateQueries({ queryKey: [`/api/v1/sellers/${sellerId}`] });
  };

  const handleSubmit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      await api.put(`/api/v1/me/reviews/${sellerId}`, {
        rating,
        comment: comment.trim() || null,
      });
      notifications.show({
        title: 'Review saved',
        message: 'Thanks for sharing your experience!',
        color: 'green',
      });
      setFormOpen(false);
      setRating(0);
      setComment('');
      refreshAfterMutation();
    } catch (err) {
      if (err?.code === 'forbidden' || err?.status === 403) {
        notifications.show({
          title: 'Not just yet',
          message:
            'You can review a princess once you have messaged her or made a special request with her.',
          color: 'yellow',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: err?.message || 'Could not save your review',
          color: 'red',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.del(`/api/v1/me/reviews/${sellerId}`);
      notifications.show({
        title: 'Review deleted',
        message: 'Your review has been removed.',
        color: 'green',
      });
      refreshAfterMutation();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Could not delete your review',
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack gap="md">
      {/* Header: title + aggregate */}
      <Group gap="sm" align="center">
        <Title order={3}>Reviews</Title>
        {ratingCount > 0 && <RatingStars value={ratingAvg} count={ratingCount} />}
      </Group>

      {/* Write / delete affordances (signed-in only) */}
      {isSignedIn && (
        <Paper withBorder p="md" radius="md">
          {formOpen ? (
            <Stack gap="sm">
              <Text fw={600}>Your review</Text>
              <Rating value={rating} onChange={setRating} size="lg" color="yellow" />
              <Textarea
                aria-label="Review comment"
                placeholder="Share your experience (optional)"
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
                maxLength={2000}
                autosize
                minRows={3}
              />
              <Group gap="sm">
                <Button
                  color="grape"
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={rating === 0}
                >
                  Submit review
                </Button>
                <Button variant="subtle" color="gray" onClick={() => setFormOpen(false)}>
                  Cancel
                </Button>
              </Group>
            </Stack>
          ) : (
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Button
                variant="light"
                color="grape"
                leftSection={<IconStar size="1rem" />}
                onClick={() => setFormOpen(true)}
              >
                Write / update review
              </Button>
              <Button
                variant="subtle"
                color="gray"
                size="compact-sm"
                onClick={handleDelete}
                loading={deleting}
              >
                Delete my review
              </Button>
            </Group>
          )}
        </Paper>
      )}

      {/* Review list */}
      {loading ? (
        <Group justify="center" p="lg">
          <Loader size="sm" />
        </Group>
      ) : error ? (
        <Text c="red" size="sm">
          {error.message || 'Could not load reviews'}
        </Text>
      ) : items.length === 0 ? (
        <Paper p="lg" withBorder radius="md">
          <Text ta="center" c="dimmed" size="sm">
            No reviews yet.
            {isSignedIn ? ' Be the first to share your experience!' : ''}
          </Text>
        </Paper>
      ) : (
        <Stack gap="sm">
          {items.map((review) => (
            <Paper key={review.id} withBorder p="md" radius="md">
              <Group align="flex-start" gap="sm" wrap="nowrap">
                <Avatar
                  src={review.reviewer_avatar_url || undefined}
                  radius="xl"
                  color="pink"
                >
                  <IconUser size="1.1rem" />
                </Avatar>
                <Stack gap={4} style={{ flex: 1 }}>
                  <Group gap="xs" justify="space-between" wrap="wrap">
                    <Text fw={600} size="sm">
                      {review.reviewer_display_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDate(review.created_at)}
                    </Text>
                  </Group>
                  <RatingStars value={review.rating} size="xs" />
                  {review.comment && (
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {review.comment}
                    </Text>
                  )}
                </Stack>
              </Group>
            </Paper>
          ))}
          {totalPages > 1 && (
            <Group justify="center" mt="xs">
              <Pagination value={page} onChange={setPage} total={totalPages} color="grape" />
            </Group>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default SellerReviews;
